import { Breadcrumb } from "@/components/ui/breadcrumb";
import { EmptyState } from "@/components/ui/feedback";
import { ErrorState, LoadingState } from "@/components/ui/system-state";
import { FreshnessMatrix } from "@/features/oversight/freshness-matrix";
import type { FreshnessState } from "@/features/oversight/freshness-matrix";

/**
 * Source freshness is observation data: it only exists once a source has
 * actually reported. Nothing on this page is derived, estimated or defaulted —
 * a wallet × source pair with no reported age renders as a dash rather than a
 * plausible-looking duration, and a scan that fails renders no matrix at all.
 *
 * The matrix itself lives in `features/oversight/freshness-matrix.tsx` so the
 * `/reconciliation` overview can render the same grid (design-port `oversight`
 * lane 1); this page owns only the route's breadcrumb and its
 * loading/empty/error/ready orchestration.
 *
 * Display only (no actions), so there is no page-spec action to bind
 * (`/monitoring/freshness` is carried as a route-spec baseline entry).
 */
export type { FreshnessCell, FreshnessRow, FreshnessState } from "@/features/oversight/freshness-matrix";

function FreshnessEmptyState() {
  return (
    <EmptyState
      title="No source freshness observations yet"
      description="No balance, transaction or event source has reported an observation for this environment, so there is nothing to compare against a freshness tolerance."
      meta="Freshness is only shown for a source that has published at least one observation. Nothing on this page is estimated."
    />
  );
}

export default function MonitoringFreshnessPage({ state = { status: "empty" } }: { state?: FreshnessState }) {
  const noRows = state.status === "empty" || (state.status === "ready" && state.rows.length === 0);

  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Source Freshness" }]} />

      {state.status === "loading" && (
        <div>
          <p className="mb-3 text-sm text-muted">Loading source freshness...</p>
          <LoadingState rows={4} />
        </div>
      )}

      {noRows && <FreshnessEmptyState />}

      {state.status === "ready" && state.rows.length > 0 && (
        <FreshnessMatrix rows={state.rows} lastScan={state.lastScan} />
      )}

      {state.status === "error" && (
        <ErrorState
          title="Source freshness could not be loaded"
          description="The freshness scan failed, so no observation is shown. A partial matrix would read as a source that reported nothing."
          details={state.supportReference ? `Support reference: ${state.supportReference}` : undefined}
        />
      )}
    </div>
  );
}
